import { Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import {
  TOAST_STATE,
  ToastService,
} from "src/app/shared/services/toast.service";
import { Market } from "src/app/shared/models/market.model";
import { AuthorizationService } from "src/app/shared/services/authorization.service";
import { MarketSelectionService } from "src/app/shared/services/market-selection.service";
import { MarketService } from "src/app/shared/services/market.service";
import { first, Observable } from "rxjs";
import { AppReadyService } from "src/app/shared/services/app-ready.service";
import { MenuStateService } from "src/app/shared/services/menu.service";
import { TegelModule } from "@scania/tegel-angular-17";
import { CommonModule } from "@angular/common";

@Component({
    selector: "app-header",
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.scss"],
    standalone: true,
    imports: [TegelModule, CommonModule]
})
export class HeaderComponent implements OnInit {
  private authService = inject(AuthorizationService);
  private marketService = inject(MarketService);
  private toast = inject(ToastService);
  private mktSelectService = inject(MarketSelectionService);
  private appReadyService = inject(AppReadyService);
  private menuState = inject(MenuStateService);

  @Input() appVersion: string = "";
  @Input() angularVersion: string = "";
  @Output() eventFromHeader = new EventEmitter<any>();

  public isAvatarOpened: boolean = false;
  public isMarketOpened: boolean = false;

  public username: string = "";
  public useremail: string | null = "";
  public userMarkets: Market[] = [];
  public userRoles: string[] = [];
  private userMarketRoles: string[] = [];

  public selectMarkets: Market[] = [];
  public selectedMarket: Market = new Market(0, "MARKETS", "", false);

  selectedMarketLabel = "MARKETS";
  
  ngOnInit(): void {
    this.appReadyService.appReady$
    .pipe(
      first()
    )
    .subscribe(() => {
      this.getUser();
    });

    this.mktSelectService.selectedMarket$.pipe(first()).subscribe((market) => {
      this.selectedMarket = market;
    });
    this.mktSelectService.selectedMarket$.subscribe((market) => {
      this.selectedMarket = market;
      this.selectedMarketLabel = market?.name ?? 'MARKETS';
    });
  }

  private getUser() {
    var userJson = sessionStorage.getItem("rtm-cpm-user");
    var user = JSON.parse(userJson!);

    this.username = user.name;
    this.useremail = user.email;
    this.userRoles = user.roles;
    this.userMarketRoles = user.markets ?? [];

    this.loadMarkets();
  }

  private loadMarkets(): void {
    this.getMarkets().subscribe({
      next: (value: Market[]) => {
        if (value && value.length > 0) {
          this.userMarkets = this.filterMarketsByUser(
            value.map(m => ({ ...m, acronym: m.acronym.replace('#','_') }))
          );
          if (this.userMarkets.length === 0) {
            this.selectedMarket = new Market(0, "MARKETS", "", false);
            this.selectedMarketLabel = "MARKETS";
            return;
          }

          const current = this.mktSelectService.getSelectedMarket();
          const fromList = this.userMarkets.find(m => m.id === current?.id)
                        ?? this.userMarkets[0];
          if (!current || current.id !== fromList.id) {
            this.mktSelectService.updateSelectedMarket(fromList);
          }

          this.selectedMarket = fromList;
          this.selectedMarketLabel = fromList.name;
        }
      },
      error: () => this.toast.showToast(TOAST_STATE.error, "Can't load markets"),
    });
  }

  private getMarkets(): Observable<Market[]> {
    return this.userRoles.includes('BRSA_ASSIST_SCANIA_IT') ? this.marketService.getAll() : this.marketService.getAllEnabled();
  }

  private filterMarketsByUser(markets: Market[]): Market[] {
    if (this.userRoles.includes('BRSA_ASSIST_SCANIA_IT')) {
      return markets;
    }

    const allowedMarketRoles = new Set(
      this.userMarketRoles.map((marketRole) => this.normalizeMarketRole(marketRole))
    );

    return markets.filter((market) =>
      allowedMarketRoles.has(this.normalizeMarketRole('MARKET_' + market.acronym))
    );
  }

  private normalizeMarketRole(value: string): string {
    return value.trim().toUpperCase().replace('#', '_');
  }

  public openMobileMenu(): void {
    this.eventFromHeader.emit({
      type: "showMobileMenu",
      value: true,
    });
  }

  public logout(): void {
    this.authService.logout();
  }

  public openAvatar() {
    this.isMarketOpened = false;
    this.isAvatarOpened = !this.isAvatarOpened;
  }

  public openMarketSelector() {
    this.isAvatarOpened = false;
    this.isMarketOpened = !this.isMarketOpened;
  }

  public onChangeMarket(selectedId: number) {
    const market = this.userMarkets.find(c => c.id === selectedId);
    if (!market) return;

    const current = this.mktSelectService.getSelectedMarket();
    if (current?.id === market.id) {
      this.isMarketOpened = false;
      return;
    }

    this.mktSelectService.setSelectedMarket(market);

    this.isMarketOpened = false;
  }

  mobileMenuAction(): void {
    this.menuState.setSignal(true);
  }
}
